<template>
  <div class="dp__overlay" @click.self="$emit('close')">
    <aside class="dp">
      <header class="dp__head">
        <div>
          <h2 class="dp__title">Документы рейса {{ trip.number }}</h2>
          <div v-if="data" class="dp__sub">
            Финансовый статус TMS: <b>{{ data.finance_status || '—' }}</b>
            <template v-if="data.registry"> · Реестр {{ data.registry.number || '' }} от {{ formatDate(data.registry.date) }}</template>
          </div>
        </div>
        <button class="dp__close" type="button" @click="$emit('close')">✕</button>
      </header>

      <div v-if="error" class="dp__error">{{ error }}</div>
      <div v-if="!data" class="dp__placeholder">Загружаем…</div>

      <template v-else>
        <div class="dp__package">
          <span :class="['tag', data.package.complete ? 'tag--ok' : 'tag--incomplete']">
            Пакет {{ data.package.complete ? 'укомплектован' : 'не укомплектован' }}
          </span>
          <button v-if="canEdit" class="dp__btn" type="button" @click="togglePackage">
            {{ data.package.complete ? 'Снять отметку' : 'Отметить комплект собран' }}
          </button>
          <span v-if="data.deliveries.length" class="dp__muted">Поставки: {{ data.deliveries.join(', ') }}</span>
        </div>

        <section v-for="section in sections" :key="section.key" class="dp__section">
          <h3 class="dp__h3">{{ section.title }}</h3>
          <table class="dp__table">
            <thead>
              <tr>
                <th>Документ</th><th v-if="section.group === 'transport'">Поставка</th><th>№</th><th>Дата</th>
                <th>ЭДО: отпр. / получ.</th><th v-if="section.group === 'transport'">Статус ЭДО</th>
                <th>Оригинал: отпр. / получ.</th><th v-if="section.group === 'transport'">Корректен</th>
                <th>Своя фирма / контрагент</th><th v-if="section.group === 'financial'">Оплата</th><th>Источник</th><th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="d in docsOf(section)" :key="d.id">
                <td>{{ kindTitle(section.group, d.kind) }}</td>
                <td v-if="section.group === 'transport'">{{ d.delivery_ref || '—' }}</td>
                <td>{{ d.number || '—' }}</td>
                <td>{{ formatDate(d.doc_date) }}</td>
                <td>{{ formatDate(d.edo_sent) }} / {{ formatDate(d.edo_received) }}</td>
                <td v-if="section.group === 'transport'">{{ d.edo_status || '—' }}</td>
                <td>{{ formatDate(d.original_sent) }} / {{ formatDate(d.original_received) }}</td>
                <td v-if="section.group === 'transport'">{{ d.is_correct === null ? '—' : (d.is_correct ? 'да' : 'нет') }}</td>
                <td>{{ d.own_company || '—' }} / {{ d.counterparty || '—' }}</td>
                <td v-if="section.group === 'financial'">{{ formatDate(d.payment_date) }}</td>
                <td>{{ d.source === 'manual' ? 'вручную' : d.source }}</td>
                <td>
                  <button v-if="canEdit" class="dp__link" type="button" @click="edit(section, d)">изменить</button>
                  <button v-if="canEdit && d.source === 'manual'" class="dp__link dp__link--bad" type="button" @click="remove(d)">удалить</button>
                </td>
              </tr>
              <tr v-if="section.key === 'customer_fin' && data.registry">
                <td>Реестр</td><td>{{ data.registry.number || '—' }}</td><td>{{ formatDate(data.registry.date) }}</td><td colspan="6"></td>
              </tr>
              <tr v-if="!docsOf(section).length && !(section.key === 'customer_fin' && data.registry)">
                <td colspan="12" class="dp__muted">Документов нет</td>
              </tr>
            </tbody>
          </table>
          <button v-if="canEdit" class="dp__btn dp__btn--sm" type="button" @click="edit(section)">+ Добавить</button>
        </section>

        <form v-if="form" class="dp__form" @submit.prevent="submit">
          <h3 class="dp__h3">{{ form.id ? 'Изменить документ' : 'Новый документ' }} — {{ form.sectionTitle }}</h3>
          <div class="dp__grid">
            <label>Тип
              <select v-model="form.data.kind" required :disabled="!!form.id">
                <option v-for="(title, key) in kinds(form.data.group)" :key="key" :value="key">{{ title }}</option>
              </select>
            </label>
            <label v-if="form.data.group === 'transport'">Поставка
              <input v-model="form.data.delivery_ref" :list="'deliveries'" placeholder="номер поставки">
              <datalist id="deliveries"><option v-for="dl in data.deliveries" :key="dl" :value="dl" /></datalist>
            </label>
            <label>Номер <input v-model="form.data.number"></label>
            <label>Дата <input v-model="form.data.doc_date" type="date"></label>
            <label>ЭДО отправлен <input v-model="form.data.edo_sent" type="date"></label>
            <label>ЭДО получен <input v-model="form.data.edo_received" type="date"></label>
            <label v-if="form.data.group === 'transport'">Статус подписания в ЭДО <input v-model="form.data.edo_status" placeholder="по титулам"></label>
            <label>Оригинал отправлен <input v-model="form.data.original_sent" type="date"></label>
            <label>Оригинал получен <input v-model="form.data.original_received" type="date"></label>
            <label v-if="form.data.group === 'transport'">Корректен
              <select v-model="form.data.is_correct"><option :value="null">—</option><option :value="true">да</option><option :value="false">нет</option></select>
            </label>
            <label>Своя фирма <input v-model="form.data.own_company"></label>
            <label>Контрагент <input v-model="form.data.counterparty"></label>
            <label v-if="form.data.group === 'financial'">Дата оплаты <input v-model="form.data.payment_date" type="date"></label>
            <label class="dp__wide">Комментарий <input v-model="form.data.comment"></label>
          </div>
          <div class="dp__actions">
            <button class="dp__btn dp__btn--primary" type="submit">Сохранить</button>
            <button class="dp__btn" type="button" @click="form = null">Отмена</button>
          </div>
        </form>
      </template>
    </aside>
  </div>
</template>

<script>
import { onMounted, ref } from 'vue'
import { registryApi, errorText } from '@/backend/registryApi.js'
import { formatDate } from './registryLogic.mjs'

const SECTIONS = [
  { key: 'customer_fin', group: 'financial', side: 'customer', title: 'Финансовые документы — заказчик' },
  { key: 'carrier_fin', group: 'financial', side: 'carrier', title: 'Финансовые документы — перевозчик' },
  { key: 'carrier_tr', group: 'transport', side: 'carrier', title: 'Транспортные документы — от перевозчика' },
  { key: 'customer_tr', group: 'transport', side: 'customer', title: 'Транспортные документы — заказчику' },
]
const FIELDS = ['kind', 'delivery_ref', 'number', 'doc_date', 'edo_sent', 'edo_received', 'edo_status', 'original_sent',
  'original_received', 'is_correct', 'own_company', 'counterparty', 'payment_date', 'comment']

export default {
  name: 'TripDocumentsPanel',
  props: { trip: { type: Object, required: true }, canEdit: Boolean },
  emits: ['close', 'changed'],
  setup(props, { emit }) {
    const data = ref(null)
    const error = ref('')
    const form = ref(null)

    async function load() {
      try {
        data.value = await registryApi.documents(props.trip.id)
      } catch (e) {
        error.value = errorText(e)
      }
    }
    const docsOf = (section) => (data.value?.documents || []).filter((d) => d.group === section.group && d.side === section.side)
    const kinds = (group) => (data.value?.kinds || {})[group] || {}
    const kindTitle = (group, kind) => kinds(group)[kind] || kind

    function edit(section, doc) {
      const base = { group: section.group, side: section.side, is_correct: null }
      for (const f of FIELDS) base[f] = doc ? doc[f] : (f === 'is_correct' ? null : '')
      if (!doc) base.kind = Object.keys(kinds(section.group))[0]
      form.value = { id: doc?.id, sectionTitle: section.title, data: base }
    }

    async function submit() {
      error.value = ''
      const payload = {}
      for (const [k, v] of Object.entries(form.value.data)) payload[k] = v === '' ? null : v
      try {
        if (form.value.id) {
          delete payload.group; delete payload.side; delete payload.kind
          await registryApi.updateDocument(props.trip.id, form.value.id, payload)
        } else {
          await registryApi.addDocument(props.trip.id, payload)
        }
        form.value = null
        await load()
      } catch (e) {
        error.value = errorText(e)
      }
    }

    async function remove(doc) {
      try {
        await registryApi.deleteDocument(props.trip.id, doc.id)
        await load()
      } catch (e) {
        error.value = errorText(e)
      }
    }

    async function togglePackage() {
      try {
        await registryApi.setPackage([props.trip.id], !data.value.package.complete)
        await load()
        emit('changed')
      } catch (e) {
        error.value = errorText(e)
      }
    }

    onMounted(load)
    return { data, docsOf, edit, error, form, formatDate, kindTitle, kinds, remove, sections: SECTIONS, submit, togglePackage }
  },
}
</script>

<style scoped>
.dp__overlay { position: fixed; inset: 0; background: rgba(17, 24, 39, 0.35); z-index: 50; display: flex; justify-content: flex-end; }
.dp { width: min(1100px, 100%); height: 100%; overflow: auto; background: #fff; padding: 20px; display: flex; flex-direction: column; gap: 14px; }
.dp__head { display: flex; justify-content: space-between; gap: 12px; }
.dp__title { margin: 0; font-size: 1.2rem; }
.dp__sub { color: #6b7280; font-size: 0.85rem; margin-top: 4px; }
.dp__close { border: 0; background: transparent; font-size: 1.2rem; cursor: pointer; }
.dp__error { border: 1px solid #fecaca; background: #fef2f2; color: #7f1d1d; border-radius: 10px; padding: 8px 12px; }
.dp__placeholder, .dp__muted { color: #6b7280; font-size: 0.85rem; }
.dp__package { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
.dp__h3 { margin: 0 0 6px; font-size: 0.95rem; }
.dp__table { border-collapse: collapse; width: 100%; font-size: 0.8rem; margin-bottom: 6px; }
.dp__table th { text-align: left; background: #f9fafb; padding: 6px; border-bottom: 1px solid #e5e7eb; }
.dp__table td { padding: 5px 6px; border-bottom: 1px solid #f3f4f6; }
.dp__btn { border: 1px solid #d1d5db; background: #fff; border-radius: 9999px; padding: 6px 12px; font: inherit; font-size: 0.82rem; cursor: pointer; }
.dp__btn--sm { padding: 3px 10px; font-size: 0.78rem; }
.dp__btn--primary { background: #4f46e5; border-color: #4f46e5; color: #fff; }
.dp__link { border: 0; background: none; color: #4f46e5; cursor: pointer; font: inherit; font-size: 0.78rem; padding: 0 4px; }
.dp__link--bad { color: #b91c1c; }
.dp__form { border: 1px solid #c7d2fe; background: #f5f7ff; border-radius: 12px; padding: 12px; }
.dp__grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 10px; }
.dp__grid label { display: flex; flex-direction: column; gap: 3px; font-size: 0.78rem; color: #374151; }
.dp__grid input, .dp__grid select { border: 1px solid #d1d5db; border-radius: 6px; padding: 5px 7px; font: inherit; font-size: 0.85rem; }
.dp__wide { grid-column: 1 / -1; }
.dp__actions { display: flex; gap: 8px; margin-top: 10px; }
.tag { display: inline-block; border-radius: 9999px; padding: 2px 10px; font-size: 0.78rem; }
.tag--ok { background: #dcfce7; color: #166534; }
.tag--incomplete { background: #fef3c7; color: #92400e; }
</style>
